import React, { useMemo, useState, useEffect } from "react";
import type {
    Chip,
    EventLite,
    PersonLite,
    ShiftLite,
    TargetType,
    FunctionLite,
    Ticket,
} from "../types";
import DropTargetCard from "./DropTargetCard";
import { formatDateTime24 } from "../utils/date";
import { canAssignTicketToShift, getShiftTicketMismatchReason } from "../utils/ticketRules";
import { getApiBase, safeJson } from "../api/http";
import { getAuthHeaders } from "../../../../api/authHelper";

const ITEMS_PER_PAGE = 25;

export default function TargetsPanel({
    tab,
    setTab,

    activeTicket,
    ignoreShiftTimeRule,
    personSearch,
    setPersonSearch,
    personChipsById,

    events,
    eventsLoading,
    selectedEventId,
    setSelectedEventId,
    shifts,
    shiftsLoading,
    refreshShifts,
    shiftChipsById,
    onOpenOverview,
    functionSearch,
    setFunctionSearch,
    functionsLoading,
    functionsError,
    refreshFunctions,
    functions,
    functionChipsById,
}: {
    tab: TargetType;
    setTab: (t: TargetType) => void;
    onOpenOverview: () => void;
    activeTicket?: Ticket | null;
        ignoreShiftTimeRule: boolean;
    personSearch: string;
    setPersonSearch: (v: string) => void;
    personChipsById: Map<number, Chip[]>;

    events: EventLite[];
    eventsLoading: boolean;
    selectedEventId: number | "";
    setSelectedEventId: (v: number | "") => void;

    shifts: ShiftLite[];
    shiftsLoading: boolean;
    refreshShifts: () => void;
    shiftChipsById: Map<number, Chip[]>;

    functionSearch: string;
    setFunctionSearch: (v: string) => void;
    functionsLoading: boolean;
    functionsError: string | null;
    refreshFunctions: (eventId: number) => Promise<void>;
    functions: FunctionLite[];
    functionChipsById: Map<number, Chip[]>;
}) {
    const [peoplePage, setPeoplePage] = useState(1);
    const [peopleTotalPages, setPeopleTotalPages] = useState(1);
    const [peopleTotalCount, setPeopleTotalCount] = useState(0);
    const [peopleData, setPeopleData] = useState<PersonLite[]>([]);
    const [peopleLoading, setPeopleLoading] = useState(false);
    const [peopleError, setPeopleError] = useState<string | null>(null);

    const [shiftPage, setShiftPage] = useState(1);
    const [functionPage, setFunctionPage] = useState(1);
    const [shiftSearch, setShiftSearch] = useState("");

    useEffect(() => {
        setPeoplePage(1);
    }, [personSearch, selectedEventId]);

    useEffect(() => {
        if (tab !== "person") return;

        let cancelled = false;

        const loadPeople = async () => {
            setPeopleLoading(true);
            setPeopleError(null);

            try {
                const base = getApiBase();

                const params = new URLSearchParams();
                params.set("page", String(peoplePage));
                params.set("pageSize", String(ITEMS_PER_PAGE));

                if (selectedEventId !== "") {
                    params.set("eventId", String(selectedEventId));
                }

                if (personSearch.trim()) {
                    params.set("search", personSearch.trim());
                }

                const res = await fetch(`${base}/api/persons?${params.toString()}`, {
                    headers: getAuthHeaders(),
                });

                if (!res.ok) {
                    throw new Error(`Kunne ikke hente personer (${res.status})`);
                }

                const data = await safeJson<any>(res);

                const arr = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.Items)
                            ? data.Items
                            : [];

                const mapped: PersonLite[] = arr
                    .map((p: any) => {
                        const rawId =
                            p.personID ??
                            p.PersonID ??
                            p.personId ??
                            p.PersonId ??
                            p.id ??
                            p.ID;

                        const personID = Number(rawId);

                        const firstName = p.firstName ?? p.FirstName ?? "";
                        const lastName = p.lastName ?? p.LastName ?? "";

                        const fullName = String(
                            p.fullName ??
                            p.FullName ??
                            p.name ??
                            p.Name ??
                            `${firstName} ${lastName}`
                        ).trim();
                        const functions = p.functions ?? p.Functions ?? [];

                        const functionName = Array.isArray(functions)
                            ? functions
                                .map((f: any) =>
                                    typeof f === "string"
                                        ? f
                                        : f.functionName ?? f.FunctionName ?? f.name ?? f.Name ?? ""
                                )
                                .filter(Boolean)
                                .join(", ")
                            : typeof functions === "string"
                                ? functions
                                : p.functionName ??
                                p.FunctionName ??
                                p.function ??
                                p.Function ??
                                undefined;

                        return {
                            personID,
                            fullName:
                                fullName ||
                                `Person #${Number.isFinite(personID) ? personID : "?"}`,
                            email: p.email ?? p.Email ?? undefined,
                            functionName,
                        };
                    })
                    .filter((x: PersonLite) => Number.isFinite(x.personID) && x.personID > 0);

                if (cancelled) return;

                setPeopleData(mapped);
                setPeopleTotalCount(data?.totalCount ?? data?.TotalCount ?? mapped.length);
                setPeopleTotalPages(data?.totalPages ?? data?.TotalPages ?? 1);
            } catch (e: any) {
                if (cancelled) return;

                console.error(e);
                setPeopleError(e?.message || "Fejl ved hentning af personer");
                setPeopleData([]);
                setPeopleTotalCount(0);
                setPeopleTotalPages(1);
            } finally {
                if (!cancelled) {
                    setPeopleLoading(false);
                }
            }
        };

        loadPeople();

        return () => {
            cancelled = true;
        };
    }, [tab, peoplePage, personSearch, selectedEventId]);

    useEffect(() => {
        setShiftPage(1);
    }, [selectedEventId, shifts.length, shiftSearch]);

    useEffect(() => {
        setFunctionPage(1);
    }, [selectedEventId, functionSearch, functions.length]);

    useEffect(() => {
        if (tab === "person") setPeoplePage(1);
        if (tab === "shift") setShiftPage(1);
        if (tab === "function") setFunctionPage(1);
    }, [tab]);

    useEffect(() => {
        setShiftSearch("");
    }, [selectedEventId]);

    const safeShifts = Array.isArray(shifts) ? shifts : [];
    const safeFunctions = Array.isArray(functions) ? functions : [];

    const filteredShifts = useMemo(() => {
        const q = shiftSearch.trim().toLowerCase();
        if (!q) return safeShifts;

        return safeShifts.filter((s) => {
            return (
                s.title?.toLowerCase().includes(q) ||
                String(s.shiftsID).includes(q) ||
                (s.start ? formatDateTime24(s.start).toLowerCase().includes(q) : false) ||
                (s.end ? formatDateTime24(s.end).toLowerCase().includes(q) : false)
            );
        });
    }, [safeShifts, shiftSearch]);

    const paginatedShifts = useMemo(() => {
        const start = (shiftPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return filteredShifts.slice(start, end);
    }, [filteredShifts, shiftPage]);

    const paginatedFunctions = useMemo(() => {
        const start = (functionPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return safeFunctions.slice(start, end);
    }, [safeFunctions, functionPage]);

    const shiftsTotalPages = Math.max(1, Math.ceil(filteredShifts.length / ITEMS_PER_PAGE));
    const functionsTotalPages = Math.max(1, Math.ceil(safeFunctions.length / ITEMS_PER_PAGE));

    function renderPagination(
        page: number,
        setPage: (page: number) => void,
        totalItems: number,
        totalPages: number
    ) {
        if (totalItems <= ITEMS_PER_PAGE) return null;

        const startItem = (page - 1) * ITEMS_PER_PAGE + 1;
        const endItem = Math.min(page * ITEMS_PER_PAGE, totalItems);

        return (
            <div
                style={{
                    marginTop: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <div style={{ color: "#666", fontSize: 13 }}>
                    Viser {startItem}-{endItem} af {totalItems}
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                        className="edit-btn"
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        style={{ opacity: page <= 1 ? 0.5 : 1 }}
                    >
                        Forrige
                    </button>

                    <div style={{ fontSize: 13, color: "#555", minWidth: 70, textAlign: "center" }}>
                        Side {page} / {totalPages}
                    </div>

                    <button
                        className="edit-btn"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages}
                        style={{ opacity: page >= totalPages ? 0.5 : 1 }}
                    >
                        Næste
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="form-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <h2 style={{ margin: 0 }}>Tildel tickets</h2>

                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className="edit-btn"
                        onClick={() => setTab("person")}
                        style={{ opacity: tab === "person" ? 1 : 0.7 }}
                    >
                        Person
                    </button>

                    <button
                        className="edit-btn"
                        onClick={() => setTab("shift")}
                        style={{ opacity: tab === "shift" ? 1 : 0.7 }}
                    >
                        Vagter
                    </button>

                    <button
                        className="edit-btn"
                        onClick={() => setTab("function")}
                        style={{ opacity: tab === "function" ? 1 : 0.7 }}
                    >
                        Funktion
                    </button>
                    <button
                        className="edit-btn"
                        style={{
                            marginLeft: 10,
                            background: "#2e7d32",
                            color: "white",
                        }}
                        onClick={onOpenOverview}
                    >
                        Ticket overview
                    </button>
                </div>
            </div>

            {tab === "person" ? (
                <>
                    <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                        <input
                            type="text"
                            placeholder="Søg person (navn, id, email, funktion)..."
                            value={personSearch}
                            onChange={(e) => setPersonSearch(e.target.value)}
                            style={{
                                flex: 1,
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>

                    {peopleLoading ? (
                        <div style={{ color: "#666", fontSize: 13, marginTop: 10 }}>
                            Loader personer...
                        </div>
                    ) : null}

                    {peopleError ? (
                        <div style={{ color: "#dc3545", fontSize: 13, marginTop: 10 }}>
                            {peopleError}
                        </div>
                    ) : null}

                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {peopleData.map((p) => {
                            const chips = personChipsById.get(p.personID) ?? [];
                            const count = chips.length;

                            return (
                                <DropTargetCard
                                    key={p.personID}
                                    droppableId={`person-${p.personID}`}
                                    title={`${p.fullName} (#${p.personID})`}
                                    subtitle={p.functionName}
                                    rightBadge={count ? `${count} tickets` : "Drop ticket"}
                                    chips={chips}
                                />
                            );
                        })}

                        {!peopleLoading && peopleData.length === 0 ? (
                            <div style={{ color: "#666", fontSize: 13 }}>
                                Ingen personer matcher søgningen.
                            </div>
                        ) : null}
                    </div>

                    {renderPagination(
                        peoplePage,
                        setPeoplePage,
                        peopleTotalCount,
                        peopleTotalPages
                    )}
                </>
            ) : null}

            {tab === "shift" ? (
                <>
                    <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: "#555" }}>
                            Event
                        </label>

                        <select
                            value={selectedEventId}
                            onChange={(e) =>
                                setSelectedEventId(e.target.value === "" ? "" : Number(e.target.value))
                            }
                            style={{
                                flex: 1,
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                            }}
                            disabled={eventsLoading}
                        >
                            <option value="">-- Vælg event --</option>
                            {events.map((ev) => (
                                <option key={ev.eventID} value={ev.eventID}>
                                    {ev.eventName}
                                </option>
                            ))}
                        </select>

                        <button className="edit-btn" onClick={refreshShifts} disabled={!selectedEventId}>
                            Refresh
                        </button>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                        <input
                            type="text"
                            placeholder="Søg vagt (titel, id, dato eller tid)..."
                            value={shiftSearch}
                            onChange={(e) => setShiftSearch(e.target.value)}
                            style={{
                                flex: 1,
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>

                    {shiftsLoading ? (
                        <div style={{ marginTop: 10, color: "#666", fontSize: 13 }}>
                            Loader vagter...
                        </div>
                    ) : null}

                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {paginatedShifts.map((s) => {
                            const chips = shiftChipsById.get(s.shiftsID) ?? [];
                            const count = chips.length;

                            const canDropHere = ignoreShiftTimeRule
                                ? true
                                : activeTicket
                                    ? canAssignTicketToShift(activeTicket, s)
                                    : true;

                            const disabledReason = ignoreShiftTimeRule
                                ? undefined
                                : activeTicket
                                    ? getShiftTicketMismatchReason(activeTicket, s) ?? undefined
                                    : undefined;

                            return (
                                <DropTargetCard
                                    key={s.shiftsID}
                                    droppableId={`shift-${s.shiftsID}`}
                                    title={s.title}
                                    subtitle={
                                        s.start && s.end
                                            ? `${formatDateTime24(s.start)} → ${formatDateTime24(s.end)}`
                                            : "—"
                                    }
                                    rightBadge={count ? `${count} tickets` : "Drop ticket"}
                                    chips={chips}
                                    disabled={!canDropHere}
                                    disabledReason={disabledReason}
                                />
                            );
                        })}

                        {!shiftsLoading && selectedEventId !== "" && filteredShifts.length === 0 ? (
                            <div style={{ color: "#666", fontSize: 13 }}>
                                Ingen vagter matcher søgningen.
                            </div>
                        ) : null}
                    </div>

                    {renderPagination(shiftPage, setShiftPage, filteredShifts.length, shiftsTotalPages)}
                </>
            ) : null}

            {tab === "function" ? (
                <>
                    <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: "#555" }}>
                            Event
                        </label>

                        <select
                            value={selectedEventId}
                            onChange={(e) =>
                                setSelectedEventId(e.target.value === "" ? "" : Number(e.target.value))
                            }
                            style={{
                                flex: 1,
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                            }}
                            disabled={eventsLoading}
                        >
                            <option value="">-- Vælg event --</option>
                            {events.map((ev) => (
                                <option key={ev.eventID} value={ev.eventID}>
                                    {ev.eventName}
                                </option>
                            ))}
                        </select>

                        <button
                            className="edit-btn"
                            onClick={() => selectedEventId !== "" && refreshFunctions(Number(selectedEventId))}
                            disabled={selectedEventId === "" || functionsLoading}
                        >
                            Refresh
                        </button>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                        <input
                            type="text"
                            placeholder="Søg funktion (navn eller id)..."
                            value={functionSearch}
                            onChange={(e) => setFunctionSearch(e.target.value)}
                            style={{
                                flex: 1,
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>

                    {functionsLoading ? (
                        <div style={{ marginTop: 10, color: "#666", fontSize: 13 }}>
                            Loader funktioner...
                        </div>
                    ) : null}

                    {functionsError ? (
                        <div style={{ marginTop: 10, color: "#dc3545", fontSize: 13 }}>
                            {functionsError}
                        </div>
                    ) : null}

                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {paginatedFunctions.map((f) => {
                            const chips = functionChipsById.get(f.functionId) ?? [];
                            const count = chips.length;

                            return (
                                <DropTargetCard
                                    key={f.functionId}
                                    droppableId={`function-${f.functionId}`}
                                    title={f.functionName}
                                    subtitle="Drop ticket her for at tildele til funktionen"
                                    rightBadge={count ? `${count} tickets` : "Drop ticket"}
                                    chips={chips}
                                />
                            );
                        })}

                        {!functionsLoading && selectedEventId !== "" && safeFunctions.length === 0 ? (
                            <div style={{ color: "#666", fontSize: 13 }}>
                                Ingen funktioner for dette event.
                            </div>
                        ) : null}

                        {selectedEventId === "" ? (
                            <div style={{ color: "#666", fontSize: 13 }}>
                                Vælg et event for at se funktioner.
                            </div>
                        ) : null}
                    </div>

                    {renderPagination(
                        functionPage,
                        setFunctionPage,
                        safeFunctions.length,
                        functionsTotalPages
                    )}
                </>
            ) : null}
        </div>
    );
}